import QRCode from "qrcode";
import { useState, type FormEvent } from "react";
import { Navigate, useLocation } from "react-router-dom";

import { Alert } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/context/AuthContext";
import { useRed } from "@/context/RedContext";
import { api, errorMessage } from "@/lib/api";

type Step = "credentials" | "totp" | "backup" | "setup" | "recovery";

export function LoginPage() {
  const { user, loading, login, verifyTotp, verifyBackupCode } = useAuth();
  // The queue outlives signing out (§2.7), so it is readable here — and this
  // is the one screen where a manager most needs to be told it is still there.
  const { stanje } = useRed();
  const ceka = stanje.ceka;
  const location = useLocation();

  const [step, setStep] = useState<Step>("credentials");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [code, setCode] = useState("");
  const [backupCode, setBackupCode] = useState("");
  const [mfaToken, setMfaToken] = useState("");
  const [secret, setSecret] = useState("");
  const [qr, setQr] = useState("");
  const [recoveryCodes, setRecoveryCodes] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  if (loading) {
    return <CentredNote>Učitavanje…</CentredNote>;
  }

  if (user) {
    const from = (location.state as { from?: string } | null)?.from ?? "/";
    return <Navigate to={from} replace />;
  }

  const run = async (action: () => Promise<void>, fallback: string) => {
    setError(null);
    setBusy(true);
    try {
      await action();
    } catch (caught) {
      setError(errorMessage(caught, fallback));
    } finally {
      setBusy(false);
    }
  };

  const submitCredentials = (event: FormEvent) => {
    event.preventDefault();
    void run(async () => {
      const result = await login(email, password);
      if (result.status === "mfa") {
        setMfaToken(result.mfaToken);
        setStep("totp");
        setPassword("");
      } else if (result.status === "mfa-setup") {
        const { data } = await api.post<{ secret: string; provisioning_uri: string }>(
          "/auth/mfa/setup",
          { mfa_token: result.mfaToken },
        );
        setMfaToken(result.mfaToken);
        setSecret(data.secret);
        setQr(await QRCode.toDataURL(data.provisioning_uri, { margin: 1 }));
        setPassword("");
        setStep("setup");
      }
    }, "Prijava nije uspjela. Pokušajte ponovno.");
  };

  const submitTotp = (event: FormEvent) => {
    event.preventDefault();
    void run(
      () => verifyTotp(mfaToken, code),
      "Potvrda nije uspjela. Pokušajte ponovno.",
    );
  };

  const submitBackupCode = (event: FormEvent) => {
    event.preventDefault();
    void run(
      () => verifyBackupCode(mfaToken, backupCode),
      "Potvrda nije uspjela. Pokušajte ponovno.",
    );
  };

  const submitSetup = (event: FormEvent) => {
    event.preventDefault();
    void run(async () => {
      const { data } = await api.post<{ codes: string[] }>("/auth/mfa/confirm", {
        mfa_token: mfaToken,
        code,
      });
      setRecoveryCodes(data.codes);
      setCode("");
      setSecret("");
      setQr("");
      setStep("recovery");
    }, "Kod nije prihvaćen.");
  };

  return (
    <main className="flex min-h-screen flex-col items-center justify-center gap-4 bg-muted px-4 py-10">
      <Card className="w-full max-w-md">
        <CardHeader>
          <CardTitle>AVM aplikacija</CardTitle>
          <CardDescription>
            {step === "credentials" && "Prijavite se svojim računom."}
            {step === "totp" &&
              "Unesite šesteroznamenkasti kod iz aplikacije za autentifikaciju."}
            {step === "backup" &&
              "Unesite jedan od rezervnih kodova koje ste spremili."}
            {step === "setup" &&
              "Za ovaj račun prvo je potrebno uključiti dvofaktorsku prijavu."}
            {step === "recovery" && "Spremite rezervne kodove prije nastavka."}
          </CardDescription>
        </CardHeader>

        <CardContent className="flex flex-col gap-4">
          {error && <Alert variant="error">{error}</Alert>}

          {/* Z-5: signing in is where a manager whose session ended lands,
              often with a queue behind him. Without this the screen looks like
              the app forgot his work; the queue survives signing out by design
              (§2.7), and saying so here is the difference between waiting and
              entering it all again. */}
          {ceka > 0 && (
            <p className="text-[var(--color-muted-foreground)]">
              <span className="broj">{ceka}</span>{" "}
              {ceka === 1 ? "unos čeka slanje" : "unosa čeka slanje"} — nakon
              prijave šalju se sami.
            </p>
          )}

          {step === "credentials" && (
            <form className="flex flex-col gap-4" onSubmit={submitCredentials}>
              <div className="flex flex-col gap-2">
                <Label htmlFor="email">Adresa e-pošte</Label>
                <Input
                  id="email"
                  type="email"
                  autoComplete="username"
                  required
                  autoFocus
                  value={email}
                  onChange={(event) => setEmail(event.target.value)}
                />
              </div>

              <div className="flex flex-col gap-2">
                <Label htmlFor="password">Lozinka</Label>
                <Input
                  id="password"
                  type="password"
                  autoComplete="current-password"
                  required
                  value={password}
                  onChange={(event) => setPassword(event.target.value)}
                />
              </div>

              <Button type="submit" disabled={busy}>
                {busy ? "Prijava u tijeku…" : "Prijava"}
              </Button>
            </form>
          )}

          {step === "totp" && (
            <form className="flex flex-col gap-4" onSubmit={submitTotp}>
              <div className="flex flex-col gap-2">
                <Label htmlFor="code">Kod iz aplikacije</Label>
                <Input
                  id="code"
                  // A numeric keypad on a phone, and no autocorrect mangling
                  // a six digit code on the way in.
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  pattern="[0-9]*"
                  maxLength={6}
                  required
                  autoFocus
                  value={code}
                  onChange={(event) => setCode(event.target.value)}
                />
              </div>

              <Button type="submit" disabled={busy}>
                {busy ? "Provjera…" : "Potvrdi"}
              </Button>

              <Button
                type="button"
                variant="ghost"
                onClick={() => {
                  setStep("backup");
                  setError(null);
                }}
              >
                Nemam pristup telefonu
              </Button>
            </form>
          )}

          {step === "backup" && (
            <form className="flex flex-col gap-4" onSubmit={submitBackupCode}>
              <div className="flex flex-col gap-2">
                <Label htmlFor="backup">Rezervni kod</Label>
                <Input
                  id="backup"
                  autoComplete="off"
                  autoCapitalize="off"
                  autoCorrect="off"
                  spellCheck={false}
                  placeholder="npr. a1b2c-d3e4f"
                  required
                  autoFocus
                  value={backupCode}
                  onChange={(event) => setBackupCode(event.target.value)}
                />
                <p className="text-sm text-muted-foreground">
                  Svaki kod vrijedi samo jednom.
                </p>
              </div>

              <Button type="submit" disabled={busy}>
                {busy ? "Provjera…" : "Prijava rezervnim kodom"}
              </Button>

              <Button
                type="button"
                variant="ghost"
                onClick={() => {
                  setStep("totp");
                  setError(null);
                }}
              >
                Natrag na kod iz aplikacije
              </Button>
            </form>
          )}

          {step === "setup" && (
            <form className="flex flex-col gap-4" onSubmit={submitSetup}>
              {qr && (
                <img
                  src={qr}
                  alt="QR kod za aplikaciju za autentifikaciju"
                  className="mx-auto h-48 w-48"
                />
              )}
              <div className="flex flex-col gap-2">
                <Label htmlFor="setup-secret">Ručni unos ključa</Label>
                <code
                  id="setup-secret"
                  className="select-all break-all rounded-md border bg-muted px-3 py-2"
                >
                  {secret}
                </code>
              </div>
              <div className="flex flex-col gap-2">
                <Label htmlFor="setup-code">Kod iz aplikacije</Label>
                <Input
                  id="setup-code"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  pattern="[0-9]*"
                  maxLength={6}
                  required
                  autoFocus
                  value={code}
                  onChange={(event) => setCode(event.target.value)}
                />
              </div>
              <Button type="submit" disabled={busy}>
                {busy ? "Potvrda…" : "Potvrdi dvofaktorsku prijavu"}
              </Button>
            </form>
          )}

          {step === "recovery" && (
            <div className="flex flex-col gap-4">
              <Alert variant="error">
                Ovi se kodovi prikazuju samo jednom. Spremite ih na sigurno mjesto.
              </Alert>
              <ul className="grid grid-cols-2 gap-2 font-mono text-sm">
                {recoveryCodes.map((recoveryCode) => (
                  <li key={recoveryCode} className="rounded bg-muted px-2 py-1 text-center">
                    {recoveryCode}
                  </li>
                ))}
              </ul>
              <Button
                onClick={() => {
                  setRecoveryCodes([]);
                  setMfaToken("");
                  setStep("credentials");
                }}
              >
                Spremio sam kodove, natrag na prijavu
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
      {/* Staging carries a copy of production; the sign-in screen is where
          somebody can still tell which of the two they are about to use. */}
      {window.location.hostname.startsWith("staging.") && (
        <p className="text-sm text-muted-foreground">
          Testno okruženje — izmjene ovdje ne vrijede za stvarni rad.
        </p>
      )}
    </main>
  );
}

function CentredNote({ children }: { children: string }) {
  return (
    <main className="flex min-h-screen items-center justify-center text-muted-foreground">
      {children}
    </main>
  );
}
